// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import { observer } from 'mobx-react';

import container from '@core/di';
import Config from '@core/config';
import Deliverable from '@shared/models/deliverable';
import CustomersStore from '@shared/stores/customers';
import OrdersStore from '@shared/stores/orders';
import { SortingType, ActivityFilter } from '@shared/constants/services';
import { getMaxDate } from '@shared/utils/common';
import { Customer } from '@shared/models/customers';
import Field from '@shared/components/Form/components/Field';
import TextField from '@shared/components/TextField';
import Select, { getOptionsFromLabels, SerializerType } from '@shared/components/Select';
import DatePicker from '@shared/components/DatePicker';
import Autocomplete, { normalize } from '@shared/components/Autocomplete';
import { ORDER_TYPES_LABELS, ORDER_FIELDS } from '@shared/constants/order';
import { CUSTOMER_FIELDS } from '@shared/constants/customer';
import { DELIVERABLE_FIELDS } from '@shared/constants/deliverable';
import Button from '@shared/components/Button';
import Flex from '@shared/components/Flex';

export interface OrderDetailsProps {
  className?: string;
  showTasks: () => any;
}

const orderTypes = getOptionsFromLabels(ORDER_TYPES_LABELS);

@observer
class OrderDetails extends React.Component<OrderDetailsProps> {
  private customersStore = container.get<CustomersStore>(CustomersStore.diToken);
  private ordersStore = container.get<OrdersStore>(OrdersStore.diToken);
  private config = container.get<Config>(Config.diToken);

  constructor(props: OrderDetailsProps) {
    super(props);

    this.ordersStore.initializeForm();
  }

  private get minDate() {
    const deliverables = Object.keys(this.ordersStore.deliverables).reduce<Array<Deliverable>>((allDeliverables, assetId) => {
      const assetDeliverables = this.ordersStore.deliverables[assetId];

      allDeliverables.push(...assetDeliverables);

      return allDeliverables;
    }, []);

    if (deliverables.length) {
      return getMaxDate(deliverables, DELIVERABLE_FIELDS.deadline);
    }

    return undefined;
  }

  private handleClearOptions() {
    this.customersStore.resetList();
  }

  private get fields() {
    
    return [
      {
        name: ORDER_FIELDS.orderName,
        label: 'Order name',
        component: (
          <TextField
            fullWidth
            placeholder="Enter order name"
            margin="none"
            field={this.ordersStore.orderDetailsForm.$(ORDER_FIELDS.orderName)}
            testAttributeArgs={{
              module: 'Order-create',
              elementDescription: 'name',
            }}
          />
        )
      },
      {
        name: ORDER_FIELDS.orderNumber,
        label: 'Order number',
        component: (
          <TextField
            fullWidth
            placeholder="Enter order number"
            margin="none"
            field={this.ordersStore.orderDetailsForm.$(ORDER_FIELDS.orderNumber)}
            testAttributeArgs={{
              module: 'Order-create',
              elementDescription: 'order-number',
            }}
          />
        )
      },
      {
        name: ORDER_FIELDS.type,
        label: 'Type',
        component: (
          <Select
            fullWidth
            serializerType={SerializerType.number}
            options={orderTypes}
            placeholder="Select type"
            field={this.ordersStore.orderDetailsForm.$(ORDER_FIELDS.type)}
            testAttributeArgs={{
              module: 'Order-create',
              elementDescription: 'types',
            }}
          />
        )
      },
      {
        name: 'deliverables',
        label: 'Deliverables',
        component: (
          <Flex alignItems="flex-start" justifyContent="space-between">
              <span>Custom</span>
              <Button
                text="Show details"
                color="primary"
                size="medium"
                onClick={this.props.showTasks}
              />
          </Flex>
        )
      },
      {
        name: ORDER_FIELDS.customerId,
        label: 'Customer',
        component: (
          <Autocomplete
            disableClearable
            filterSelectedOptions
            showSelectedValues
            showInputValue
            multiple={false}
            placeholder="Search customer"
            field={this.ordersStore.orderDetailsForm.$(ORDER_FIELDS.customerId)}
            options={normalize(this.customersStore.list)}
            loading={this.customersStore.loading.list}
            testAttributeArgs={{
              module: 'Order-create',
              elementDescription: 'customers',
            }}
            clearOptions={this.handleClearOptions.bind(this)}
            onInputChange={this.handleInputChange.bind(this)}
            onValueChange={this.handleValueChange.bind(this)}
          />
        )
      },
      {
        name: ORDER_FIELDS.dueDate,
        label: 'Due date',
        component: (
          <DatePicker
            endOfUnit="day"
            minDate={this.minDate}
            testAttributeArgs={{ module: 'Order-create', elementDescription: 'due-date' }}
            field={this.ordersStore.orderDetailsForm.$(ORDER_FIELDS.dueDate)}
            placeholder="Due date"
          />
        )
      },
      {
        name: ORDER_FIELDS.notes,
        label: 'Notes',
        component: (
          <TextField
            fullWidth
            multiline
            placeholder="Enter note"
            margin="none"
            field={this.ordersStore.orderDetailsForm.$(ORDER_FIELDS.notes)}
            maxRows={4}
            testAttributeArgs={{
              module: 'Order-create',
              elementDescription: 'notes',
            }}
          />
        )
      }
    ];
  }

  private handleInputChange = (searchKeyword: string) => {
    this.customersStore.getList({
      pagination: { page: 0, pageSize: this.config.get().autocomplete.itemsCount },
      sorting: { type: SortingType.asc, field: CUSTOMER_FIELDS.name },
      filtering: { searchKeyword, activityFilter: ActivityFilter.all, isBlocked: false }
    });
  }

  private handleValueChange = (customerId: Customer['id']) => {
    const values = this.ordersStore.orderDetailsForm.values();

    if (customerId != values[ORDER_FIELDS.customerId]) {
      this.ordersStore.clearAssets();
    }
  }

  render() {
    const classes = withStyles.getClasses(this.props);
    return this.fields.map((field) => <Field {...field} key={field.name} classes={{label: classes.fieldLabel}} />);
  }
}

const OrderDetailsStyled = withStyles(
  OrderDetails, 
  (theme, props) => ({
    "root": {},
    "fieldLabel": {
      width: '30%',
    }
  })
);

export default OrderDetailsStyled;